import React, { useEffect, useRef } from 'react';
import { Image } from 'tamagui';
import { useNavigate } from 'react-router-dom';

import { 
  CobuContainer, 
  CobuStack, 
  CobuActionButtons,
  CobuHeader,
  CobuBackButton,
  CobuText,
  PerkDetailSkeleton
} from '@cobu/ui';
import { Strings } from '../../../constants/Strings';
import { ReferralTabs} from '../../../components/referral/ReferralTabs';
import { useReferralDetail } from '../../../hooks/useReferralDetail';
import { showErrorMessage } from '../../../utils/messageToast';
import { logger } from '../../../utils/logger';

export default function ReferralDetailScreen() {
  const navigate = useNavigate();
  const referralStrings = Strings.get().referral.detail;
  
  const {
    referralOffer,
    primaryImageUri,
    isLoading,
    isSharing,
    error,
    canShare,
    canRefer,
    hasValidData,
    hasNoOffer,
    handleShare,
    handleReferNow,
    handleRetry,
  } = useReferralDetail();

  useEffect(() => {
    logger.log('referralOffer', referralOffer);
  }, [referralOffer]);

  // React.useEffect(() => {
  //   if (referralOffer) {
  //     analyticsService.trackEvent(AnalyticsEvent.REFERRAL_DETAILS_MOUNTED, {
  //       referralOffer: referralOffer,
  //     });
  //   }
  // }, [referralOffer]);

  // Error state
  const hasShownErrorRef = useRef(false);
  useEffect(() => {
    if (!hasValidData && !hasNoOffer && !hasShownErrorRef.current) {
      showErrorMessage(referralStrings.errors.loadError);
      hasShownErrorRef.current = true;
    }
  }, [hasValidData, hasNoOffer]);

  // Loading state
  if (isLoading) {
    return (
      <PerkDetailSkeleton />
    );
  }

  if (hasNoOffer) {
    return (
      <CobuContainer f={1} bg="$white">
        <CobuHeader title={referralStrings.webViewTitle} renderLeft={() => <CobuBackButton onPress={() => navigate(-1)} />} />
        <CobuStack f={1} ai="center" jc="center" gap="$3" px="$6">
          <CobuText variant="heading" color="$cobuDarkBlue" ta="center">
            {referralStrings.empty.title}
          </CobuText>
          <CobuText variant="body" color="$colorSecondary" ta="center">
            {referralStrings.empty.subtitle}
          </CobuText>
        </CobuStack>
      </CobuContainer>
    );
  }

  if (!hasValidData) {
    return null;
  }

  // UI Components
  const renderHeader = () => {
    const bannerTitle = referralOffer?.bannerTitle || referralOffer?.title;
    return (
      <CobuStack bg="$cobuDarkBlue" h={'$44'} ai="center" jc="center" px="$4">
        {primaryImageUri ? (
          <Image
            source={{ uri: primaryImageUri }}
            w="100%"
            h="100%"
            objectFit="cover"
          />
        ) : (
          !!bannerTitle && (
            <CobuText variant="heading" fontSize="$7" fontWeight="bold" color="$white" ta="center">
              {bannerTitle}
            </CobuText>
          )
        )}
      </CobuStack>
    );
  };

  const renderActionButtons = () => {
    const buttons = [
      {
        title: isSharing ? referralStrings.buttons.sharing : referralStrings.buttons.shareLink,
        variant: 'secondary' as const,
        onPress: handleShare,
        disabled: !canShare || isSharing,
      },
      {
        title: referralStrings.buttons.referNow,
        variant: 'primary' as const,
        onPress: handleReferNow,
        disabled: !canRefer,
      },
    ];

    return (
      <CobuStack 
        px="$4" 
        py="$3" 
        btw={1} 
        btc="$backgroundMuted" 
        mb="$4"
      >
        <CobuActionButtons 
          buttons={buttons}
          responsive={false}
          direction="horizontal"
          gap="$2"
        />
      </CobuStack>
    );
  };

  return (
    <CobuContainer f={1} bg="$white">
      <CobuHeader title={referralStrings.webViewTitle} renderLeft={() => <CobuBackButton onPress={() => navigate(-1)} />} />
      <CobuStack f={1}>
        {renderHeader()}
        <CobuStack f={1}>
          {referralOffer && <ReferralTabs referralOffer={referralOffer} />}
        </CobuStack>
      </CobuStack>
      {renderActionButtons()}
    </CobuContainer>
  );
} 
